"use client";

import React, { useState } from "react";
import Breadcrumb from "@/components/Breadcrumbs/Breadcrumb";
import { useAuth } from "@/contexts/AuthContext";
import { useDialog } from "@/hooks/useDialog";
import { toKeyValue } from "@/utils/convert";
import Busqueda from "./components/Busqueda";
import Table from "./components/Table";
import DeudasAPI from "@/api/Deudas";
import formatDate from "@/utils/formatDate";

/**
 * Reimpresión de Títulos Pagados.
 *
 * Esta página NO necesitó cambios para el tema oscuro: todos los
 * componentes hijos (Breadcrumb, Busqueda, Table -> TableThree) ya
 * usan tokens semánticos que reaccionan al tema. Esa es la idea de
 * trabajar "por composición": si la base está bien, las pantallas
 * casi no necesitan reglas dark: propias.
 *
 * Lo único decorativo aquí es el `<div className="grid ...">` que
 * organiza la búsqueda arriba y la tabla debajo. No tiene color
 * propio, así que es transparente al tema.
 */

const EXCLUDED_RUBROS = new Set(["44", "59"]);

const getTitulosPayload = (response) => {
  const payload = response?.data?.data ?? response?.data ?? [];

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.Datos)) return payload.Datos;
  if (Array.isArray(payload?.informacion)) return [{ informacion: payload.informacion }];
  if (Array.isArray(payload?.Informacion)) return [{ informacion: payload.Informacion }];

  return [];
};

const getInformacionTitulos = (response) => {
  const payload = getTitulosPayload(response);
  const first = payload[0] ?? {};

  if (Array.isArray(first?.informacion)) return first.informacion;
  if (Array.isArray(first?.Informacion)) return first.Informacion;

  if (payload.some((item) => item?.contrapartida || item?.Contrapartida)) {
    return payload;
  }

  return [];
};

const mapTitulosPagados = (response) => {
  const informacion = getInformacionTitulos(response);

  return Object.values(
    informacion
      .filter((item) => !EXCLUDED_RUBROS.has(String(item?.id_rubro ?? item?.IdRubro ?? "")))
      .reduce((acc, item) => {
        const contrapartida = item?.contrapartida ?? item?.Contrapartida;
        const fechaPago = item?.fecha_pago ?? item?.FechaPago ?? item?.fechaPago;
        const clavepredial = item?.clavepredial ?? item?.ClavePredial;

        if (!contrapartida || !fechaPago || !clavepredial) return acc;

        const key = `${contrapartida}-${fechaPago}-${clavepredial}`;

        if (!acc[key]) {
          acc[key] = {
            contrapartida,
            fecha_pago: formatDate.formatDateTime(fechaPago),
            clavepredial,
            tipotitulo: item?.tipotitulo ?? item?.TipoTitulo ?? item?.nombre ?? "Título pagado",
            rubros: "",
          };
        }

        return acc;
      }, {}),
  );
};

const ReimpresionTitulosPagados = () => {
  const { user, loading: authLoading } = useAuth();
  const { show } = useDialog();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);

  const onSearch = async (fechaE, fechaV) => {
    if (!user?.cedularuc) {
      show({
        type: "warning",
        title: "Sesión no disponible",
        message: "No se pudo obtener la identificación del usuario en sesión. Recargue la página e intente nuevamente.",
      });
      return;
    }

    setIsLoading(true);

    try {
      const resp = await DeudasAPI.getConsultaTitulo(fechaE, fechaV, user.cedularuc);
      const resultado = mapTitulosPagados(resp);

      setData(resultado);

      if (resultado.length === 0) {
        show({
          type: "info",
          title: "Sin resultados",
          message: "No se encontraron títulos pagados para el rango de fechas seleccionado.",
        });
      }
    } catch (error) {
      setData([]);

      if (error.response?.data) {
        show({
          type: "warning",
          title: error.response.data.error ?? "Error",
          message: error.response.data.msg ?? "Ha ocurrido un error, intente de nuevo por favor",
        });
      } else {
        const { name, message } = error;
        show({ type: "error", title: name, message });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onPrintTitulo = async (idtitulo) => {
    if (!user?.cedularuc) {
      show({
        type: "warning",
        title: "Sesión no disponible",
        message: "No se pudo obtener la identificación del usuario en sesión.",
      });
      return;
    }

    try {
      const resp = await DeudasAPI.getImprimirTitulo(idtitulo, user.cedularuc);

      if (resp.status !== 200) {
        show({
          type: "warning",
          title: "Error",
          message: "No se ha podido imprimir su título",
        });
        return;
      }

      const { Certificado } = toKeyValue(resp.data.data);

      if (Certificado) {
        const certificado = `data:application/pdf;base64,${Certificado}`;
        const link = document.createElement("a");
        link.href = certificado;
        link.download = `titulo_${idtitulo}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      if (error.response?.data) {
        let message = error.response.data.msg ?? "Ha ocurrido un error, intente de nuevo por favor";

        if (error.response.data.msg === "TITULO NO EXISTE") {
          message = "El título no está pagado en su totalidad y por lo tanto no se puede imprimir";
        }

        show({
          type: "warning",
          title: error.response.data.error ?? "Error",
          message,
        });
      } else {
        const { name, message } = error;
        show({ type: "error", title: name, message });
      }
    }
  };

  return (
    <>
      <Breadcrumb pageName="Reimpresión de Títulos Pagados" />
      <div className="grid grid-cols-1 gap-4">
        <Busqueda isLoading={isLoading || authLoading} onSearch={onSearch} />
        <Table
          data={data}
          isLoading={isLoading}
          onPrintTitulo={onPrintTitulo}
          page={page}
          setPage={setPage}
        />
      </div>
    </>
  );
};

export default ReimpresionTitulosPagados;
